import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/app/AuthContext';
import { useSelection } from '@/app/SelectionContext';
import {
  useConfirmImport,
  useImportChanges,
  useImportDetail,
  useImportQuality,
  useImports,
  useMapSheet,
  useReopenPeriod,
  useReimportImport,
  useRollbackImport,
  useUnmappedRows,
  useUploadImport,
} from '@/app/hooks';
import type { ImportDetail } from '@/types/schemas';
import { PageHeader } from '@/components/PageHeader';
import { Card, SectionTitle } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Spinner } from '@/components/LoadingScreen';
import { importsService } from '@/services/imports.service';
import { CategoryMappingStep } from '@/features/imports/CategoryMappingStep';
import { ApiRequestError } from '@/services/api';
import { Permission } from '@/types/permission';
import { NO_DATA, formatAmd, formatChange, momChange, toNumber } from '@/utils/format';
import { font, v } from '@/theme/tokens';

type WizardError = { code: string; message: string; details?: Record<string, unknown> };

/**
 * How an import status reads to a person.
 *
 * NEEDS_MAPPING is amber, not red: the file parsed, the sheets matched and the
 * periods were found — someone just has to say what a few lines are. Red is
 * reserved for an import that genuinely failed.
 */
const STATUS_LABELS: Record<string, string> = {
  UPLOADED: 'Загружен',
  PARSING: 'Разбор',
  VALIDATING: 'Проверка',
  NEEDS_MAPPING: 'Требуется сопоставление',
  READY: 'Готов к подтверждению',
  CONFIRMED: 'Подтверждён',
  FAILED: 'Ошибка',
  ROLLED_BACK: 'Откачен',
};

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

function statusTone(status: string): 'positive' | 'warning' | 'negative' | 'neutral' {
  if (status === 'CONFIRMED') return 'positive';
  if (status === 'FAILED') return 'negative';
  if (status === 'NEEDS_MAPPING') return 'warning';
  if (status === 'ROLLED_BACK') return 'neutral';
  return 'neutral';
}

const CHANGE_TABS = [
  ['ALL', 'Все'],
  ['NEW', 'Новые'],
  ['UPDATED', 'Изменённые'],
  ['REMOVED', 'Удалённые'],
  ['UNCHANGED', 'Без изменений'],
] as const;

export function ImportsPage() {
  const { can, user } = useAuth();
  const { units, periods } = useSelection();
  const inputRef = useRef<HTMLInputElement>(null);

  const list = useImports();
  const upload = useUploadImport();
  const confirm = useConfirmImport();
  const rollback = useRollbackImport();
  const reimport = useReimportImport();
  const mapSheet = useMapSheet();
  const reopen = useReopenPeriod();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [changeTab, setChangeTab] = useState<string>('ALL');
  const [error, setError] = useState<WizardError | null>(null);
  const [done, setDone] = useState(false);
  const [reopenFor, setReopenFor] = useState<string | null>(null);
  const [reopenReason, setReopenReason] = useState('');
  const [rollbackFor, setRollbackFor] = useState<string | null>(null);
  const [reimportFor, setReimportFor] = useState<string | null>(null);

  const detail = useImportDetail(activeId ?? undefined);
  const quality = useImportQuality(activeId ?? undefined);
  const changes = useImportChanges(activeId ?? undefined);
  const unmapped = useUnmappedRows(activeId ?? undefined);

  const isOwner = user?.role.code === 'OWNER';
  const active = detail.data;
  const report = quality.data;
  const unknownSheets = active?.sheets.filter((s) => !s.mapped_business_unit_id) ?? [];
  const unmappedCount = unmapped.data?.length ?? 0;
  const blocking =
    (report?.errors?.length ?? 0) > 0 || unknownSheets.length > 0 || unmappedCount > 0;

  const asError = (e: unknown): WizardError =>
    e instanceof ApiRequestError
      ? { code: e.code, message: e.message, details: e.details }
      : { code: 'unknown', message: 'Непредвиденная ошибка' };

  const handleFile = async (file: File) => {
    setError(null);
    setDone(false);
    try {
      const result = await upload.mutateAsync(file);
      setActiveId(result.id);
    } catch (e) {
      setError(asError(e));
    }
  };

  const handleConfirm = async () => {
    if (!activeId) return;
    setError(null);
    try {
      await confirm.mutateAsync(activeId);
      setDone(true);
    } catch (e) {
      setError(asError(e));
    }
  };

  const handleReopen = async () => {
    if (!reopenFor || !reopenReason.trim()) return;
    const period = periods.find(
      (p) => `${p.year}-${String(p.month).padStart(2, '0')}` === reopenFor,
    );
    if (!period) return;
    try {
      await reopen.mutateAsync({ periodId: period.id, reason: reopenReason });
      setReopenFor(null);
      setReopenReason('');
      setError(null); // period reopened — the user confirms again, explicitly
    } catch (e) {
      setError(asError(e));
    }
  };

  const unitName = (id: string | null) => units.find((u) => u.id === id)?.name;
  const periodName = (id: string) => {
    const period = periods.find((p) => p.id === id);
    return period ? `${period.year}-${String(period.month).padStart(2, '0')}` : id.slice(0, 8);
  };
  const fieldStyle = {
    width: '100%', padding: '9px 11px', border: `1px solid ${v('line')}`,
    borderRadius: 9, background: v('surface'), color: v('ink'), fontSize: 13.5, fontFamily: 'inherit',
  };

  const visibleChanges = (changes.data ?? []).filter(
    (c) => changeTab === 'ALL' || c.change_type === changeTab,
  );

  return (
    <div style={{ animation: 'fade-in 0.35s ease' }}>
      <PageHeader
        title="Импорты"
        subtitle="Загрузка и история финансовых отчётов"
        actions={
          can(Permission.UPLOAD_IMPORT) && (
            <Button variant="primary" onClick={() => inputRef.current?.click()}>
              <Icon name="upload" size={15} />
              Загрузить отчёт
            </Button>
          )
        }
      />

      <input
        ref={inputRef} type="file" accept=".xlsx,.xlsm" style={{ display: 'none' }}
        aria-label="Файл XLSX"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleFile(file);
          event.target.value = '';
        }}
      />

      {/* ---- special error states ---- */}
      {error?.code === 'DUPLICATE_IMPORT' && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Icon name="doc" size={20} color={v('warning')} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>Этот файл уже загружался</div>
              <div style={{ fontSize: 13, color: v('ink-sub'), marginTop: 4 }}>{error.message}</div>
              {error.details && (
                <div style={{ fontSize: 12.5, color: v('ink-muted'), marginTop: 6 }}>
                  Версия {String(error.details.version ?? '')} ·{' '}
                  {error.details.uploaded_at ? new Date(String(error.details.uploaded_at)).toLocaleString('ru-RU') : ''}
                </div>
              )}
              {error.details?.existing_import_id ? (
                <Button
                  style={{ marginTop: 10 }}
                  onClick={() => {
                    setActiveId(String(error!.details!.existing_import_id));
                    setError(null);
                  }}
                >
                  Открыть существующий импорт
                </Button>
              ) : null}
            </div>
          </div>
        </Card>
      )}

      {error?.code === 'PERIOD_CLOSED' && (
        <Card style={{ marginBottom: 16, borderColor: v('warning') }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Icon name="alert" size={20} color={v('warning')} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>Период закрыт</div>
              <div style={{ fontSize: 13, color: v('ink-sub'), marginTop: 4 }}>
                Импорт затрагивает закрытые периоды:{' '}
                {Array.isArray(error.details?.periods) ? (error.details!.periods as string[]).join(', ') : ''}
              </div>
              {!isOwner && (
                <div style={{ fontSize: 13, color: v('ink-muted'), marginTop: 8 }}>
                  Обратитесь к владельцу для переоткрытия периода.
                </div>
              )}
              {isOwner && !reopenFor && Array.isArray(error.details?.periods) && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {(error.details!.periods as string[]).map((key) => (
                    <Button key={key} onClick={() => setReopenFor(key)}>
                      Переоткрыть {key}
                    </Button>
                  ))}
                </div>
              )}
              {isOwner && reopenFor && (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }}>
                  <label htmlFor="reopen-reason" style={{ fontSize: 12.5, fontWeight: 600, color: v('ink-sub') }}>
                    Причина переоткрытия {reopenFor} (обязательно)
                  </label>
                  <textarea
                    id="reopen-reason" rows={2} value={reopenReason}
                    onChange={(e) => setReopenReason(e.target.value)}
                    placeholder="Получен исправленный отчёт от бухгалтера"
                    style={{ ...fieldStyle, resize: 'vertical' as const }}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      variant="primary"
                      disabled={!reopenReason.trim() || reopen.isPending}
                      onClick={() => void handleReopen()}
                    >
                      Переоткрыть период
                    </Button>
                    <Button onClick={() => setReopenFor(null)}>Отмена</Button>
                  </div>
                  <div style={{ fontSize: 12, color: v('ink-muted') }}>
                    После переоткрытия импорт НЕ подтверждается автоматически — нажмите «Подтвердить импорт» ещё раз.
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {error?.code === 'DUPLICATE_CATEGORY_CONFLICT' && (
        <Card style={{ marginBottom: 16, borderColor: v('warning') }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Icon name="alert" size={20} color={v('warning')} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: v('warning') }}>Конфликт данных</div>
              <div style={{ fontSize: 13, color: v('ink-sub'), marginTop: 4 }}>
                Несколько строк отчёта дают разные суммы для одной статьи. Подтверждение
                недоступно, пока это не устранено в файле.
              </div>
              {Array.isArray(error.details?.conflicts) && (
                <div style={{ marginTop: 10, fontSize: 12.5 }}>
                  {(error.details!.conflicts as Array<Record<string, unknown>>)
                    .slice(0, 8)
                    .map((conflict, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '6px 0',
                          borderBottom: `1px solid ${v('line-soft')}`,
                        }}
                      >
                        <span style={{ fontFamily: font.mono }}>{String(conflict.period ?? '')}</span>
                        {' · '}
                        {(conflict.candidates as Array<Record<string, unknown>> | undefined)
                          ?.map((c) => `${String(c.label ?? '')} = ${String(c.value ?? '—')}`)
                          .join('  /  ')}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {error && !['DUPLICATE_IMPORT', 'PERIOD_CLOSED', 'DUPLICATE_CATEGORY_CONFLICT'].includes(error.code) && (
        <Card style={{ marginBottom: 16, borderColor: v('negative') }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Icon name="alert" size={18} color={v('negative')} />
            <div>
              <div style={{ fontWeight: 700, color: v('negative') }}>Ошибка импорта</div>
              <div style={{ fontSize: 13, color: v('ink-sub'), marginTop: 4 }}>{error.message}</div>
            </div>
          </div>
        </Card>
      )}

      {upload.isPending && (
        <Card style={{ marginBottom: 16 }}>
          <Spinner label="Чтение книги, распознавание листов и статей…" />
        </Card>
      )}

      {/* ---- active import wizard ---- */}
      {active && (
        <Card style={{ marginBottom: 16 }}>
          <SectionTitle
            right={
              <span style={{ display: 'inline-flex', gap: 8 }}>
                <Badge tone="neutral">Ревизия {active.revision}</Badge>
                <Badge
                  tone={active.quality_status === 'SUCCESS' ? 'positive' : active.quality_status === 'WARNING' ? 'warning' : 'negative'}
                >
                  {active.quality_status}
                </Badge>
              </span>
            }
          >
            {active.filename}
          </SectionTitle>

          {active.scope_label && (
            <div style={{ fontSize: 13, color: v('ink-sub'), marginBottom: 12 }}>
              Отчётный диапазон: <b>{active.scope_label}</b> · hash {active.file_hash.slice(0, 10)}…
            </div>
          )}

          {/* Wizard progress */}
          {(() => {
            const confirmed = done || active.status === 'CONFIRMED';
            const steps: [string, boolean][] = [
              ['Загрузка', true],
              ['Листы', unknownSheets.length === 0],
              ['Статьи', !unmapped.isPending && unmappedCount === 0],
              ['Качество', Boolean(report) && (report?.errors?.length ?? 0) === 0],
              ['Изменения', (changes.data?.length ?? 0) > 0 || confirmed],
              ['Подтверждение', confirmed],
            ];
            const current = steps.findIndex(([, ok]) => !ok);
            return (
              <div aria-label="Этапы импорта" style={{ display: 'flex', gap: 6, margin: '4px 0 14px' }}>
                {steps.map(([label, ok], index) => (
                  <div key={label} style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ height: 4, borderRadius: 999, background: ok ? v('positive') : index === current ? v('accent') : v('line-soft') }} />
                    <div style={{ fontSize: 10, marginTop: 4, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: index === current ? 700 : 500, color: ok ? v('positive') : index === current ? v('accent-ink') : v('ink-muted') }}>
                      {ok ? '✓ ' : ''}{label}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Sheets */}
          <div style={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: v('ink-muted'), margin: '14px 0 8px' }}>
            Листы
          </div>
          {active.sheets.map((sheet) => {
            const mapped = units.find((u) => u.id === sheet.mapped_business_unit_id);
            return (
              <div key={sheet.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: `1px solid ${v('line')}`, borderRadius: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                <Icon name={mapped?.type === 'PRODUCTION_KITCHEN' ? 'factory' : 'building'} size={18} color={v('ink-sub')} />
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{sheet.sheet_name}</div>
                  <div style={{ fontSize: 12, color: v('ink-muted') }}>
                    {mapped ? `→ ${mapped.name}` : 'Объект не определён'}
                    {sheet.detected_periods?.length ? ` · ${sheet.detected_periods.length} мес.` : ''}
                  </div>
                </div>
                {!mapped && can(Permission.MANAGE_MAPPING) && (
                  <select
                    aria-label={`Объект для листа ${sheet.sheet_name}`}
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) {
                        void mapSheet.mutateAsync({ id: active.id, sheetId: sheet.id, businessUnitId: e.target.value });
                      }
                    }}
                    style={{ ...fieldStyle, width: 220 }}
                  >
                    <option value="" disabled>Выбрать объект…</option>
                    {units.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                )}
                <Badge tone={mapped ? 'positive' : 'warning'}>
                  {mapped ? 'Сопоставлен' : 'Нужно сопоставление'}
                </Badge>
              </div>
            );
          })}

          {/* Categories */}
          <div style={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: v('ink-muted'), margin: '14px 0 8px' }}>
            Статьи
          </div>
          <CategoryMappingStep importId={active.id} />

          {/* Quality */}
          {report && (
            <>
              <div style={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: v('ink-muted'), margin: '14px 0 8px' }}>
                Качество данных
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(110px, 100%), 1fr))', gap: 10 }}>
                {[
                  [report.recognized_rows ?? 0, 'распознано', v('positive')],
                  [report.total_rows ?? 0, 'строк всего', v('ink-sub')],
                  [(report.unknown_categories ?? []).length, 'новых статей', v('warning')],
                  [report.external_links ?? 0, 'внешних ссылок', v('warning')],
                  [report.formula_errors ?? 0, 'ошибок формул', v('negative')],
                  [report.future_formula_warnings ?? 0, 'в будущих месяцах', v('ink-muted')],
                  [report.empty_values ?? 0, 'пустых ячеек', v('ink-muted')],
                ].map(([value, label, color]) => (
                  <div key={label as string} style={{ padding: 12, border: `1px solid ${v('line')}`, borderRadius: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, fontFamily: font.mono, color: color as string }}>{value as number}</div>
                    <div style={{ fontSize: 11, color: v('ink-muted'), marginTop: 2 }}>{label as string}</div>
                  </div>
                ))}
              </div>
              {(report.unknown_categories ?? []).length > 0 && (
                <div style={{ background: v('warning-soft'), color: v('warning'), borderRadius: 10, padding: '10px 12px', fontSize: 13, marginTop: 10 }}>
                  Новые статьи (будут сохранены как raw, сопоставьте при необходимости):{' '}
                  {(report.unknown_categories ?? []).slice(0, 8).join(', ')}
                  {(report.unknown_categories ?? []).length > 8 ? ' …' : ''}
                </div>
              )}
              {(report.future_formula_warnings ?? 0) > 0 && (
                <div style={{ background: v('panel'), color: v('ink-sub'), borderRadius: 10, padding: '10px 12px', fontSize: 13, marginTop: 10 }}>
                  {report.future_formula_warnings} ошибок формул в неактивных будущих периодах — импорт не блокируют.
                </div>
              )}
              {(report.errors ?? []).length > 0 && (
                <div style={{ background: v('negative-soft'), color: v('negative'), borderRadius: 10, padding: '10px 12px', fontSize: 13, marginTop: 10 }}>
                  Блокирующие ошибки: {(report.errors ?? []).length}. Подтверждение недоступно до их устранения.
                </div>
              )}
            </>
          )}

          {/* Changes */}
          {changes.data && changes.data.length > 0 && (
            <>
              <div style={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: v('ink-muted'), margin: '16px 0 8px' }}>
                Изменения ({changes.data.length})
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                {CHANGE_TABS.map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setChangeTab(key)}
                    style={{
                      border: `1px solid ${changeTab === key ? v('accent') : v('line')}`,
                      background: changeTab === key ? v('accent-soft') : 'transparent',
                      color: changeTab === key ? v('accent-ink') : v('ink-sub'),
                      fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div style={{ maxHeight: 320, overflowY: 'auto' }} className="scroll-area">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      {['Объект', 'Период', 'Было', 'Стало', 'Δ %', ''].map((h, i) => (
                        <th key={i} style={{ textAlign: i >= 2 && i <= 4 ? 'right' : 'left', padding: '6px 8px', fontSize: 11, color: v('ink-muted'), borderBottom: `1px solid ${v('line')}`, position: 'sticky', top: 0, background: v('surface') }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleChanges.slice(0, 200).map((change, index) => {
                      const delta = momChange(toNumber(change.new_value), toNumber(change.old_value));
                      return (
                        <tr key={index}>
                          <td style={{ padding: '6px 8px', borderBottom: `1px solid ${v('line-soft')}` }}>{unitName(change.business_unit_id) ?? '—'}</td>
                          <td style={{ padding: '6px 8px', borderBottom: `1px solid ${v('line-soft')}`, fontFamily: font.mono }}>{change.period_key ?? periodName(change.financial_period_id)}</td>
                          <td style={{ padding: '6px 8px', borderBottom: `1px solid ${v('line-soft')}`, textAlign: 'right', fontFamily: font.mono, color: v('ink-muted') }}>
                            {change.old_value === null ? NO_DATA : formatAmd(toNumber(change.old_value))}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: `1px solid ${v('line-soft')}`, textAlign: 'right', fontFamily: font.mono, fontWeight: 600 }}>
                            {change.new_value === null ? NO_DATA : formatAmd(toNumber(change.new_value))}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: `1px solid ${v('line-soft')}`, textAlign: 'right', fontFamily: font.mono, color: v('ink-sub') }}>
                            {delta == null ? NO_DATA : formatChange(delta)}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: `1px solid ${v('line-soft')}` }}>
                            <Badge tone={change.change_type === 'NEW' ? 'positive' : change.change_type === 'REMOVED' ? 'negative' : change.change_type === 'UPDATED' ? 'accent' : 'neutral'}>
                              {change.change_type}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Confirm / result */}
          {done || active.status === 'CONFIRMED' ? (
            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: v('positive'), fontWeight: 700 }}>
                <Icon name="check" size={18} color={v('positive')} />
                Импорт подтверждён — показатели пересчитаны
              </span>
              <Link to="/" style={{ fontSize: 13, fontWeight: 600 }}>Открыть обзор →</Link>
              <Link to="/pnl" style={{ fontSize: 13, fontWeight: 600 }}>Открыть ОПУ →</Link>
            </div>
          ) : (
            can(Permission.CONFIRM_IMPORT) && (
              <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="primary" disabled={blocking || confirm.isPending} onClick={() => void handleConfirm()}>
                  {confirm.isPending ? 'Импорт…' : 'Подтвердить импорт'}
                </Button>
                {blocking && (
                  <span style={{ fontSize: 12.5, color: v('warning') }}>
                    {unknownSheets.length > 0 ? 'Сопоставьте все листы. ' : ''}
                    {unmappedCount > 0 ? `Необходимо сопоставить ${unmappedCount} статей. ` : ''}
                    {(report?.errors ?? []).length > 0 ? 'Устраните блокирующие ошибки.' : ''}
                  </span>
                )}
                <Button onClick={() => { setActiveId(null); setError(null); }}>Закрыть</Button>
              </div>
            )
          )}
        </Card>
      )}

      {/* ---- history ---- */}
      {list.isPending && <Spinner />}
      {list.error && (
        <ErrorState
          message={list.error instanceof Error ? list.error.message : 'Ошибка загрузки'}
          onRetry={() => void list.refetch()}
        />
      )}

      {list.data && list.data.length === 0 && !activeId && !upload.isPending && (
        // The header already offers the upload action; a second button here
        // would be the same thing twice.
        <EmptyState
          title="Импортов пока нет"
          description="Каждая загрузка XLSX сохраняется как отдельная ревизия с оригиналом файла, отчётом о качестве данных и списком изменений."
          icon="inbox"
          action={null}
        />
      )}

      {list.data?.map((job) => (
        <Card key={job.id} padding={16} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <Icon name="doc" size={20} color={v('ink-sub')} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{job.filename}</span>
                {job.scope_label && <Badge tone="neutral">{job.scope_label}</Badge>}
                <Badge tone="neutral">Ревизия {job.revision}</Badge>
                <Badge tone={statusTone(job.status)}>{statusLabel(job.status)}</Badge>
              </div>
              <div style={{ fontSize: 12.5, color: v('ink-muted'), marginTop: 3 }}>
                {new Date(job.uploaded_at).toLocaleString('ru-RU')} · качество {job.quality_status}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button onClick={() => { setActiveId(job.id); setDone(job.status === 'CONFIRMED'); }} className="rt-link" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
                Детали
              </button>
              <a href={importsService.originalUrl(job.id)} style={{ fontSize: 13, fontWeight: 600 }}>Оригинал</a>
              {isOwner && job.status === 'CONFIRMED' && (
                <button onClick={() => setRollbackFor(job.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: v('negative'), fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
                  Откат
                </button>
              )}
              {/* Re-running the file is how a corrected catalogue reaches an
                  import that was already undone. Owner-only, and the backend
                  refuses it for anything but a rolled-back import. */}
              {isOwner && job.status === 'ROLLED_BACK' && (
                <button onClick={() => setReimportFor(job.id)} className="rt-link" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
                  Переимпортировать
                </button>
              )}
            </div>
          </div>
          {reimportFor === job.id && (
            <div style={{ marginTop: 12, padding: 12, background: v('panel'), borderRadius: 10, fontSize: 13 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Переимпортировать «{job.filename}»?</div>
              <div style={{ color: v('ink-sub'), marginBottom: 10 }}>
                Исходный файл будет обработан заново с текущим справочником статей. Создастся новый
                импорт, который нужно будет подтвердить — значения сейчас не изменятся.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="primary"
                  disabled={reimport.isPending}
                  onClick={() => {
                    void reimport
                      .mutateAsync(job.id)
                      .then((fresh: ImportDetail) => {
                        setReimportFor(null);
                        setActiveId(fresh.id);
                        setDone(false);
                      })
                      .catch((e: unknown) => setError(asError(e)));
                  }}
                >
                  Переимпортировать
                </Button>
                <Button onClick={() => setReimportFor(null)}>Отмена</Button>
              </div>
            </div>
          )}
          {rollbackFor === job.id && (
            <div style={{ marginTop: 12, padding: 12, background: v('panel'), borderRadius: 10, fontSize: 13 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Откатить ревизию {job.revision}?</div>
              <div style={{ color: v('ink-sub'), marginBottom: 10 }}>
                Значения вернутся к предыдущей подтверждённой версии. Действие попадёт в журнал аудита. Данные не удаляются.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="primary"
                  disabled={rollback.isPending}
                  onClick={() => {
                    void rollback.mutateAsync(job.id).then(() => setRollbackFor(null)).catch((e) => setError(asError(e)));
                  }}
                >
                  Откатить
                </Button>
                <Button onClick={() => setRollbackFor(null)}>Отмена</Button>
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
